import { isEmpty, get } from 'lodash';
import axios from 'axios';
import { getUgcData } from '@fiverr-private/personalized_content';
import { TranslationState } from '../../../hooks/useTranslationToggle.types';
import { SourceInfo } from '../machineTranslation/buildSourceInfo/types';
import { shouldTranslate } from '../machineTranslation/utils';
import { TranslationResponse } from './personalizdContent.types';

export const translateUgcContent = async ({
    ugcMap,
    targetLocale,
    sourceInfo,
}: {
    ugcMap: TranslationState['ugcMap'];
    targetLocale: string;
    sourceInfo: SourceInfo;
}): Promise<TranslationState['ugcMap']['translated']> => {
    const translatableContent = extractTranslatableContent(ugcMap, targetLocale);

    if (!translatableContent || !shouldTranslate(targetLocale)) {
        return ugcMap.original;
    }

    const { data: translationResponse }: { data: TranslationResponse } = await axios.post(
        '/tailored_homepage/ugc/translate',
        {
            text: translatableContent,
            targetLocale,
            sourceInfo,
        }
    );

    return buildTranslatedPersonalizedContent(translationResponse, ugcMap);
};

const isGigsComponent = (component: any): boolean => !isEmpty(get(component, 'items.gigs', null));

const extractTranslatableContent = (ugcMap: TranslationState['ugcMap'], targetLocale: string) => {
    const translatableContent: { [key: string]: any } = {};
    const personalizedUgcData = ugcMap.original?.personalizedContent;

    Object.values<any[]>(personalizedUgcData?.sections ?? {}).forEach((componentsArr) => {
        componentsArr.forEach((component) => {
            if (isGigsComponent(component)) {
                const translatableGigsContent = getUgcData(component.items.gigs, targetLocale);

                Object.assign(translatableContent, translatableGigsContent);
            }
        });
    });

    return translatableContent;
};

const buildTranslatedPersonalizedContent = (
    translationResponse: TranslationResponse,
    ugcMap: TranslationState['ugcMap']
) => {
    const { translatedBlob } = translationResponse;

    const buildTranslatedGig = (gig: any) => ({ ...gig, ...translatedBlob[gig.gig_id] });
    const buildTranslatedComponent = (component: any) => ({
        ...component,
        items: {
            ...component.items,
            gigs: component.items.gigs.map(buildTranslatedGig),
        },
    });

    const sectionsWithTranslatedGigs: { [key: string]: any[] } = Object.fromEntries(
        Object.entries<Record<string, any>>(ugcMap.original?.personalizedContent.sections ?? {}).map(
            ([sectionName, componentsArr]) => [
                sectionName,
                componentsArr.map((component) =>
                    !isGigsComponent(component) ? component : buildTranslatedComponent(component)
                ),
            ]
        )
    );

    return { personalizedContent: { sections: sectionsWithTranslatedGigs } };
};
